import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { loadContent, ProductContent } from '../utils/contentLoader';
import './HomePage.css';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<ProductContent[]>([]);
  const [loading, setLoading] = useState(true);

  // Refs for scroll animations
  const categoriesSectionRef = useRef<HTMLElement>(null);
  const featuredSectionRef = useRef<HTMLElement>(null);
  const aboutSectionRef = useRef<HTMLElement>(null);
  const ctaSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const loadFeaturedProducts = async () => {
      try {
        const products = await loadContent('products') as ProductContent[];
        setFeaturedProducts(products.filter(product => product.featured).slice(0, 3));
        setLoading(false);
      } catch (error) {
        console.error('Error loading featured products:', error);
        setLoading(false);
      }
    };

    loadFeaturedProducts();
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1, // Trigger when 10% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Stop observing once animated
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sectionsToObserve = [
      categoriesSectionRef.current,
      featuredSectionRef.current,
      aboutSectionRef.current,
      ctaSectionRef.current,
    ].filter(Boolean) as HTMLElement[]; // Filter out null refs and assert type

    sectionsToObserve.forEach(section => {
      observer.observe(section);
    });

    return () => {
      sectionsToObserve.forEach(section => {
        observer.unobserve(section);
      });
      observer.disconnect();
    };
  }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

  return (
    <div className="home-page">
      <section className="hero" aria-labelledby="hero-heading">
        <div className="hero-content">
          <img src="/images/logo_transparent.png" alt="The Caffeinated Crumb Logo" className="hero-logo" />
          <h1 id="hero-heading">The Caffeinated Crumb</h1>
          <p className="hero-subtitle">Homemade Bakery</p>
          <p className="hero-description">Artisanal baked goods crafted with quality ingredients, attention to detail, and a dash of creativity</p>
          <div className="hero-cta">
            <Link to="/products" className="btn btn-primary">
              Explore Our Treats
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            </Link>
            <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Product Categories Showcase */}
      <section ref={categoriesSectionRef} className="product-categories scroll-animate fade-in-up-init" aria-labelledby="categories-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Handcrafted Goodness</span>
            <h2 id="categories-heading">Discover Our Specialties</h2>
            <p className="section-description">From custom cookies to corporate gifts, find the perfect treat for any event</p>
          </div>

          <div className="categories-grid">
            {/* Category 1: Custom Cookies */}
            <div className="category-card">
              <div className="category-image">
                <img src="/images/products/unicorn-cookies.jpg" alt="Decorative custom cookie designs" loading="lazy" />
              </div>
              <div className="category-overlay">
                <h3>Custom Cookies</h3>
                <p>Personalized treats for any occasion</p>
                <Link to="/products" className="btn btn-light-outline">View Collection</Link>
              </div>
            </div>

            {/* Category 2: Corporate Gifts */}
            <div className="category-card">
              <div className="category-image">
                <img src="/images/catering-lt-1.jpg" alt="Corporate gift options" loading="lazy" />
              </div>
              <div className="category-overlay">
                <h3>Corporate Gifts</h3>
                <p>Impress clients and colleagues</p>
                <Link to="/catering" className="btn btn-light-outline">Learn More</Link>
              </div>
            </div>

            {/* Category 3: Seasonal Treats */}
            <div className="category-card">
              <div className="category-image">
                <img src="/images/products/chocolate-chip-mm.jpg" alt="Seasonal themed treats" loading="lazy" />
              </div>
              <div className="category-overlay">
                <h3>Seasonal Treats</h3>
                <p>Special items for holidays & seasons</p>
                <Link to="/products?tag=seasonal" className="btn btn-light-outline">View Seasonal</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={featuredSectionRef} className="featured-products scroll-animate fade-in-up-init" aria-labelledby="featured-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Fan Favorites</span>
            <h2 id="featured-heading">Popular Picks You'll Love</h2>
            <p className="section-description">Discover our most popular handcrafted delights, perfect for any occasion</p>
          </div>
          
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Preparing fresh treats...</p>
            </div>
          ) : (
            <div className="grid">
              {featuredProducts && featuredProducts.map(product => (
                <div className="product-card" key={product.slug}>
                  <img 
                    src={product.image} 
                    alt={product.title} 
                    className="product-image"
                    loading="lazy"
                  />
                  <div className="product-content">
                    <h3>{product.title}</h3>
                    <p>{product.description ? product.description.split('\n')[0] : 'No description available'}</p>
                    <Link to={`/products/${product.slug}`} className="btn btn-small btn-primary">
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="view-all">
            <Link to="/products" className="btn btn-outline">
              View All Products
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <section ref={aboutSectionRef} className="about-section scroll-animate fade-in-up-init" aria-labelledby="about-heading">
        <div className="container">
          <div className="about-content">
            <span className="section-subtitle">Our Story</span>
            <h2 id="about-heading">About The Caffeinated Crumb</h2>
            <p>
              We are a local home bakery dedicated to creating delicious treats using quality ingredients. 
              Each of our baked goods is made with love and attention to detail, just like you would make at home,
              but with our special touch that sets us apart.
            </p>
            <p>
              From cookies and cakes to bread and pastries, we offer a wide variety of homemade goods 
              that are perfect for any occasion. Whether you're looking for something for everyday enjoyment 
              or a special celebration, we've got you covered.
            </p>
            <Link to="/catering" className="btn btn-primary">
              Learn About Catering
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <section ref={ctaSectionRef} className="cta-section scroll-animate fade-in-up-init" aria-labelledby="cta-heading">
        <div className="container">
          <h2 id="cta-heading">Ready to Place an Order?</h2>
          <p>Contact us today to discuss your needs or to place an order for our delicious baked goods. We're here to help make your event, celebration, or everyday moment a little sweeter.</p>
          <Link to="/contact" className="btn btn-light">
            Get in Touch
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
              <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
              <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
